import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Avatar,
  Button,
  TextField,
  Collapse,
  Pagination
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import SecurityIcon from "@mui/icons-material/Security";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { styles } from "./style";
import { useNavigate } from "react-router-dom";
import AuthService from "../../../../api/AuthService";

import { formatFormDate, formatDate as sharedFormatDate } from "../../../../utils/DateFormat";

const ProfileChangesPage = ({ data = [], onStatusChange }) => {
  const navigate = useNavigate();
  const [expandedRow, setExpandedRow] = useState(0);
  const [itemComments, setItemComments] = useState({});
  const [overallComments, setOverallComments] = useState({});
  const [historyData, setHistoryData] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const historyRowsPerPage = 10;

  useEffect(() => {
    setHistoryPage(1);
    let active = true;
    const fetchHistory = async () => {
      const currentEmp = data[expandedRow]?.employee?._id;
      if (!currentEmp) {
        setHistoryData([]);
        return;
      }
      try {
        const res = await AuthService.getUpdateHistory(currentEmp);
        if (active && res?.success) {
          setHistoryData(res.data || []);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchHistory();
    return () => { active = false; };
  }, [expandedRow, data]);

  const handleItemCommentChange = (idx, field, val) => {
    setItemComments(prev => ({...prev, [`${idx}-${field}`]: val}));
  };

  const handleOverallCommentChange = (idx, val) => {
    setOverallComments(prev => ({...prev, [idx]: val}));
  };

  const approveItem = (idx, changeField) => {
     const comment = itemComments[`${idx}-${changeField}`] || "";
     const commentsMap = comment ? { [changeField]: comment } : {};
     const reqId = data[idx]._id;
     onStatusChange && onStatusChange(reqId, "approve", [changeField], commentsMap, "");
  };

  const rejectItem = (idx, changeField) => {
     const comment = itemComments[`${idx}-${changeField}`] || "";
     const commentsMap = comment ? { [changeField]: comment } : {};
     const reqId = data[idx]._id;
     onStatusChange && onStatusChange(reqId, "reject", [changeField], commentsMap, "");
  };

  const approveAll = (idx) => {
     const overall = overallComments[idx] || "";
     const reqId = data[idx]._id;
     const allFields = data[idx].changes.filter(c => c.status === "pending").map(c => c.field);
     const commentsMap = {};
     allFields.forEach(f => {
       if (itemComments[`${idx}-${f}`]) {
         commentsMap[f] = itemComments[`${idx}-${f}`];
       }
     });
     onStatusChange && onStatusChange(reqId, "approve", allFields, commentsMap, overall);
  };

  const rejectAll = (idx) => {
     const overall = overallComments[idx] || "";
     const reqId = data[idx]._id;
     const allFields = data[idx].changes.filter(c => c.status === "pending").map(c => c.field);
     const commentsMap = {};
     allFields.forEach(f => {
       if (itemComments[`${idx}-${f}`]) {
         commentsMap[f] = itemComments[`${idx}-${f}`];
       }
     });
     onStatusChange && onStatusChange(reqId, "reject", allFields, commentsMap, overall);
  };

  const formatDate = (isoString) => {
    if (!isoString) return "N/A";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    const timePart = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    return `${sharedFormatDate(isoString)} ${timePart}`;
  };

  const formatFieldInfo = (fieldStr) => {
    if (!fieldStr) return "Field";
    const name = fieldStr.split('.').pop();
    return name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  const formatValue = (val) => {
    if (Array.isArray(val)) return val.join(", ");
    return val !== undefined && val !== null ? String(val) : "N/A";
  };

  const handleBack = () => {
    navigate(-1);
  };

  const toggleExpand = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  return (
    <Box sx={styles.pageWrap}>
      {/* Header */}
      <Box sx={styles.headerRow}>
        <Box sx={styles.titleArea}>
          <Box sx={{ cursor: "pointer", display: "flex", alignItems: "center" }} onClick={handleBack}>
            <ArrowBackIcon sx={{ mr: 1, color: "#2C2C2C" }} />
          </Box>
          <Box>
            <Typography sx={styles.mainTitle}>Review Profile Changes</Typography>
          </Box>
        </Box>
      </Box>
      <Typography sx={styles.subTitle}>Review and approve or reject individual changes</Typography>

      <Box sx={{ mt: 3 }}>
        {data.length === 0 ? (
          <Typography sx={{ textAlign: "center", mt: 4, color: "#6b7280" }}>No profile change requests.</Typography>
        ) : data.map((item, index) => {
          const isExpanded = expandedRow === index;
          const pendingChangesList = item.changes?.filter(c => c.status === "pending") || [];

          return (
            <Box key={item._id || index} sx={styles.card}>
              <Box 
                sx={{ ...styles.profileHeader, cursor: "pointer" }} 
                onClick={() => toggleExpand(index)}
              >
                <Box sx={styles.employeeInfo}>
                  <Avatar sx={{ width: 45, height: 45, backgroundColor: "#000" }} />
                  <Box sx={styles.infoCol}>
                    <Typography sx={styles.empName}>{item?.employee?.name || "Employee"}</Typography>
                    <Typography sx={styles.empId}>{item?.employee?.alias || item?.employee?._id || "ID"}</Typography>
                  </Box>
                </Box>

                <Box sx={styles.infoCol}>
                  <Typography sx={styles.infoLabel}>Requested on</Typography>
                  <Typography sx={styles.infoValue}>{formatDate(item.requestedAt)}</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: "20px" }}>
                  <Box sx={styles.infoCol}>
                    <Typography sx={styles.infoLabel}>Pending Changes</Typography>
                    <Typography sx={styles.infoValueBlue}>{pendingChangesList.length} fields</Typography>
                  </Box>
                  {isExpanded ? <ExpandLessIcon sx={{ color: "#7e7e7e" }} /> : <ExpandMoreIcon sx={{ color: "#7e7e7e" }} />}
                </Box>
              </Box>

              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Grid container spacing={3} sx={{ mt: 1, pt: 2, borderTop: "1px solid #eaeaea" }}>
                  {/* Left Side: Review Changes */}
                  <Grid item xs={12} md={8}>
                    <Typography sx={styles.sectionTitle}>
                      Review Changes ({pendingChangesList.length})
                    </Typography>

                  {pendingChangesList.length === 0 && (
                    <Typography sx={{ fontSize: "14px", color: "#6b7280", mt: 2, mb: 4 }}>
                      All changes have been reviewed.
                    </Typography>
                  )}

                  {pendingChangesList.map((change, cIdx) => (
                    <Box sx={styles.changeItemBox} key={cIdx}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography sx={styles.fieldName}>{formatFieldInfo(change.field)}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={styles.comparisonRow}>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{...styles.valueOldStrikethrough, wordBreak: "break-word", textDecoration: change.oldValue === undefined || change.oldValue === null || change.oldValue === "" ? "none" : "line-through"}}>{formatValue(change.oldValue)}</Typography>
                              <Typography sx={styles.valueLabel}>Current</Typography>
                            </Box>
                            <ArrowRightAltIcon sx={{ ...styles.arrowIcon, ml: 1, mr: 1 }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{...styles.valueTextGreen, wordBreak: "break-word"}}>{formatValue(change.newValue)}</Typography>
                              <Typography sx={styles.valueLabel}>Requested</Typography>
                            </Box>
                          </Box>
                          <Box sx={styles.commentsArea}>
                            <Typography sx={styles.commentLabel}>Comments (Optional)</Typography>
                            <TextField 
                              placeholder="Add comments..." 
                              variant="outlined" 
                              size="small" 
                              value={itemComments[`${index}-${change.field}`] || ''}
                              onChange={(e) => handleItemCommentChange(index, change.field, e.target.value)}
                              sx={styles.commentInput} 
                            />
                            <Typography sx={styles.charCount}>{(itemComments[`${index}-${change.field}`] || '').length}/200</Typography>
                          </Box>
                        </Grid>
                        
                        {/* Actions Right Side */}
                        <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "flex-start", gap: 1 }}>
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                            <Typography sx={{ ...styles.requestDate, mb: 1 }}>
                              Requested on <br />
                              {formatDate(item.requestedAt)}
                            </Typography>
                            <Box sx={styles.actionArea}>
                              <Button variant="outlined" sx={styles.rejectBtn} onClick={() => rejectItem(index, change.field)}>Reject</Button>
                              <Button variant="outlined" sx={styles.approveBtnOutline} onClick={() => approveItem(index, change.field)}>Approve</Button>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}

                  {/* Overall Comments Box */}
                  {pendingChangesList.length > 0 && (
                    <Box sx={styles.changeItemBox}>
                       <Typography sx={styles.commentLabel}>Overall Comments (Optional)</Typography>
                       <TextField 
                          placeholder="Add your overall comments..." 
                          variant="outlined" 
                          size="small" 
                          fullWidth
                          value={overallComments[index] || ''}
                          onChange={(e) => handleOverallCommentChange(index, e.target.value)}
                          sx={styles.commentInput} 
                        />
                        <Typography sx={styles.charCount}>{(overallComments[index] || '').length}/500</Typography>
                        <Box sx={styles.footerActions}>
                          <Button variant="outlined" sx={styles.rejectBtn} onClick={() => rejectAll(index)}>Reject All</Button>
                          <Button variant="contained" sx={styles.approveBtnSolid} onClick={() => approveAll(index)}>Approve All ({pendingChangesList.length})</Button>
                        </Box>
                    </Box>
                  )}
                </Grid>

                  {/* Right Side: Request History */}
                  <Grid item xs={12} md={4}>
                    <Box sx={styles.summaryPanel}>
                      <Box sx={styles.summaryHeaderRow}>
                        <Typography sx={styles.summaryTitle}>Request History</Typography>
                        <Box sx={styles.summaryPendingCount}>{historyData.filter(h => h.status === "pending").length} Pending</Box>
                      </Box>

                      {historyData
                        .slice((historyPage - 1) * historyRowsPerPage, historyPage * historyRowsPerPage)
                        .map((change, cIdx) => (
                        <Box sx={{ ...styles.summaryItem, borderBottom: cIdx === historyData.length - 1 ? "none" : "1px solid #f3f4f6" }} key={cIdx}>
                          <Typography sx={styles.summaryFieldName}>{formatFieldInfo(change.field)}</Typography>
                          <Box sx={styles.summaryValuesRow}>
                              <Typography sx={{...styles.summaryValOld, wordBreak: "break-word", textDecoration: change.oldValue === undefined || change.oldValue === null || change.oldValue === "" ? "none" : "line-through"}}>{formatValue(change.oldValue)}</Typography>
                              <ArrowRightAltIcon sx={{ color: "#d1d5db", fontSize: "16px", mx: 0.5 }} />
                            <Typography sx={{...styles.summaryValNew, wordBreak: "break-all"}}>{formatValue(change.newValue)}</Typography>
                            <Box sx={{
                                ...styles.pendingTag,
                                backgroundColor: change.status === "rejected" ? "#fee2e2" : change.status === "accepted" ? "#d1fae5" : "#fef3c7",
                                color: change.status === "rejected" ? "#dc2626" : change.status === "accepted" ? "#059669" : "#d97706",
                                textTransform: "capitalize"
                              }}
                            >
                               {change.status}
                            </Box>
                          </Box>
                        </Box>
                      ))}

                      {historyData && historyData.length > historyRowsPerPage && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, pb: 2 }}>
                          <Pagination 
                            count={Math.ceil(historyData.length / historyRowsPerPage)} 
                            page={historyPage} 
                            onChange={(e, val) => setHistoryPage(val)} 
                            color="primary" 
                            size="small"
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ProfileChangesPage;
